import { useNavigate } from 'react-router-dom';
import { articles, categories, faqs, testimonials } from '@/mocks/jiese';
import SEOHead from '@/components/feature/SEOHead';

const featuredArticles = articles.filter((a) => a.featured);
const recentArticles = articles.filter((a) => !a.featured).slice(0, 6);

export default function HomePage() {
  const navigate = useNavigate();

  const navigateTo = (path: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(path);
  };

  return (
    <>
      <SEOHead
        title="戒色完全指南 - 科学戒色方法与成功案例"
        description="戒色完全指南提供科学系统的戒色方法、心理调节技巧与实用工具，帮助有戒色需求的人群克服成瘾、重建自律，改善身心健康，实现真正的戒色成功。"
        keywords="戒色,戒色方法,戒色打卡,戒色心得,戒色多少天有效果,戒色后身体变化,戒色成功经验"
        canonicalUrl="https://purifypath.github.io/"
        ogImage="https://readdy.ai/api/search-image?query=Serene%20zen%20garden%20with%20warm%20sunlight%2C%20minimalist%20aesthetic%2C%20peaceful%20meditation%20space%2C%20soft%20beige%20and%20cream%20tones%2C%20bamboo%20and%20wooden%20elements%2C%20tranquil%20atmosphere%2C%20healing%20and%20self%20improvement%20concept&width=1200&height=630&seq=jiese-og-home&orientation=landscape"
        breadcrumbList={[{ name: '首页', url: 'https://purifypath.github.io/' }]}
        faqData={faqs}
      />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20serene%20minimalist%20zen%20garden%20at%20sunrise%2C%20warm%20golden%20light%20filtering%20through%20bamboo%20leaves%2C%20smooth%20pebbles%20and%20moss%2C%20peaceful%20and%20meditative%20atmosphere%2C%20soft%20beige%20cream%20and%20sage%20green%20color%20palette%2C%20abstract%20artistic%20style%2C%20healing%20and%20renewal%20concept&width=1600&height=900&seq=jiese-hero-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/20"></div>

          <div className="relative z-10 w-full max-w-[800px] mx-auto px-4 text-center">
            <h1
              className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              科学戒色，<br className="md:hidden" />重塑自律人生
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-8 max-w-[600px] mx-auto leading-relaxed">
              基于认知行为科学与神经科学研究的系统化戒色方案，帮助数千人走出成瘾困境，找回身心健康与生活掌控力。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigateTo('/category/jiese-fangfa')}
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-base font-medium hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                探索戒色方法
              </button>
              <button
                onClick={() => navigateTo('/category/jiese-daka')}
                className="px-8 py-3 bg-white/15 text-white border border-white/30 rounded-full text-base font-medium hover:bg-white/25 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                开始每日打卡
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 md:gap-8 mt-12 max-w-[550px] mx-auto">
              {[
                { value: '10,000+', label: '戒色同行者' },
                { value: '200+', label: '科学文章' },
                { value: '89%', label: '读者好评率' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-white/60 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                探索戒色知识体系
              </h2>
              <p className="text-sm text-foreground-600 mt-2">从多个维度系统地学习戒色知识与方法</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => navigateTo(`/category/${cat.slug}`)}
                  className="group bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 rounded-lg p-5 md:p-6 text-center transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                    <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-1 whitespace-nowrap">{cat.name}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed hidden sm:block">{cat.description}</p>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  精选推荐
                </h2>
                <p className="text-sm text-foreground-600 mt-1">编辑精选的高质量戒色文章</p>
              </div>
              <button
                onClick={() => navigateTo('/category/jiese-fangfa')}
                className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
              >
                查看全部 <i className="ri-arrow-right-line text-sm"></i>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {featuredArticles.map((article) => (
                <article
                  key={article.id}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  onClick={() => navigateTo(`/article/${article.slug}`)}
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-3 left-3 px-2.5 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-full">
                      {article.categoryName}
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="text-base md:text-lg font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors" style={{ fontFamily: 'var(--font-heading)' }}>
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 mb-3 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {article.date}</span>
                      <span className="flex items-center gap-1"><i className="ri-time-line"></i> {article.readTime}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline / Steps Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                戒色成功路线图
              </h2>
              <p className="text-sm text-foreground-600 mt-2">科学验证的四阶段戒色路径，每一步都有明确的目标和方法</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {[
                { step: '01', title: '认知觉醒', desc: '了解色情成瘾的科学机制，建立正确的戒色认知，为后续行动打下坚实的理论基础。', icon: 'ri-lightbulb-flash-line' },
                { step: '02', title: '行为重塑', desc: '通过运动、冥想、新爱好等健康行为填补空白，重建大脑的奖励机制。', icon: 'ri-run-line' },
                { step: '03', title: '环境净化', desc: '系统性清理数字与物理空间中的触发因素，打造支持戒色的外部环境。', icon: 'ri-shield-check-line' },
                { step: '04', title: '长期巩固', desc: '建立可持续的自律生活方式，将戒色从"坚持"转化为日常习惯。', icon: 'ri-trophy-line' },
              ].map((item) => (
                <div key={item.step} className="relative bg-background-100 rounded-lg p-6 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-bold mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-14 md:py-20 bg-accent-50">
          <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                戒色成功者说
              </h2>
              <p className="text-sm text-foreground-600 mt-2">来自真实戒色者的心得分享，他们的经历证明改变是可能的</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {testimonials.map((t, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-4">
                    <img src={t.avatar} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                      <p className="text-xs text-accent-600 font-medium">已戒色 {t.days} 天</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed italic">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  最新文章
                </h2>
                <p className="text-sm text-foreground-600 mt-1">持续更新的戒色知识与实践经验</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {recentArticles.map((article) => (
                <article
                  key={article.id}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  onClick={() => navigateTo(`/article/${article.slug}`)}
                >
                  <div className="relative w-full h-44 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <span className="inline-block px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full mb-2">
                      {article.categoryName}
                    </span>
                    <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors" style={{ fontFamily: 'var(--font-heading)' }}>
                      {article.title}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-600 mb-3 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {article.date}</span>
                      <span className="flex items-center gap-1"><i className="ri-time-line"></i> {article.readTime}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[800px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                常见问题
              </h2>
              <p className="text-sm text-foreground-600 mt-2">关于戒色的高频问题与科学解答</p>
            </div>

            <div className="flex flex-col gap-3">
              {faqs.map((faq, idx) => (
                <details key={idx} className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm md:text-base font-medium text-foreground-950 pr-4">{faq.question}</span>
                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 group-open:rotate-45 transition-transform duration-300">
                      <i className="ri-add-line text-sm"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20sunrise%20over%20a%20peaceful%20mountain%20landscape%2C%20soft%20golden%20light%2C%20layered%20misty%20hills%2C%20abstract%20minimalist%20style%2C%20warm%20beige%20cream%20and%20soft%20green%20tones%2C%20hope%20and%20new%20beginning%20concept&width=1600&height=600&seq=jiese-cta-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/35"></div>
          <div className="relative z-10 w-full max-w-[600px] mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              准备好开始你的戒色之旅了吗？
            </h2>
            <p className="text-sm md:text-base text-white/75 mb-8 leading-relaxed">
              每一天的坚持，都是在为更好的自己投资。从今天开始，迈出改变的第一步。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigateTo('/category/jiese-daka')}
                className="px-8 py-3 bg-accent-500 text-background-50 rounded-full text-base font-medium hover:bg-accent-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                立即开始打卡
              </button>
              <button
                onClick={() => navigateTo('/category/jiese-fangfa')}
                className="px-8 py-3 bg-white/15 text-white border border-white/30 rounded-full text-base font-medium hover:bg-white/25 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                浏览全部文章
              </button>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}